import React, { useState, useEffect, useCallback } from 'react';
import { useSubmissions } from '../SubmissionsContext';
import { API, apiFetch } from '../api';

const REGISTER_URL = 'https://care.capnet.co.uk/register';

const s = {
  page: { padding: '20px 16px 24px', maxWidth: '600px', margin: '0 auto' },
  heading: { fontSize: '20px', fontWeight: '700', color: '#222', marginBottom: '4px' },
  sub: { fontSize: '13px', color: '#888', marginBottom: '20px' },
  badge: {
    display: 'inline-block', background: '#fce8ef', color: '#DB3267',
    borderRadius: '20px', padding: '2px 10px', fontSize: '12px', fontWeight: '700', marginLeft: '8px',
  },
  qrCard: {
    background: '#fff', borderRadius: '12px', padding: '20px',
    marginBottom: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap',
  },
  qrImage: { width: '120px', height: '120px', borderRadius: '8px', flexShrink: 0 },
  qrInfo: { flex: 1, minWidth: '180px' },
  qrTitle: { fontSize: '15px', fontWeight: '700', color: '#222', marginBottom: '4px' },
  qrSub: { fontSize: '12px', color: '#888', marginBottom: '10px', lineHeight: '1.5' },
  qrUrl: { fontSize: '11px', color: '#DB3267', fontFamily: 'monospace', wordBreak: 'break-all' },
  tabs: { display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' },
  tab: { padding: '7px 16px', borderRadius: '20px', fontSize: '13px', fontWeight: '600', background: '#fff', color: '#888', border: '1px solid #e0e0e0', cursor: 'pointer' },
  tabActive: { background: '#DB3267', color: '#fff', border: 'none' },
  card: {
    background: '#fff', borderRadius: '12px', padding: '16px 18px',
    marginBottom: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
    borderLeft: '4px solid #e0e0e0',
  },
  cardPending:  { borderLeft: '4px solid #fb8c00' },
  cardApproved: { borderLeft: '4px solid #2e7d32' },
  cardRejected: { borderLeft: '4px solid #e53935', opacity: 0.6 },
  topRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' },
  nameRow: { display: 'flex', alignItems: 'center', gap: '8px' },
  name: { fontSize: '15px', fontWeight: '700', color: '#222' },
  meta: { fontSize: '12px', color: '#aaa', marginBottom: '10px' },
  actions: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  approveBtn: { padding: '7px 16px', background: '#e8f5e9', color: '#2e7d32', borderRadius: '6px', fontSize: '12px', fontWeight: '700', border: '1.5px solid #a5d6a7', cursor: 'pointer' },
  rejectBtn:  { padding: '7px 16px', background: '#fff', color: '#888', borderRadius: '6px', fontSize: '12px', fontWeight: '600', border: '1px solid #e0e0e0', cursor: 'pointer' },
  editBtn:    { padding: '7px 14px', background: '#f5f4f8', color: '#555', borderRadius: '6px', fontSize: '12px', fontWeight: '600', border: '1px solid #e0e0e0', cursor: 'pointer' },
  statusBadge: { fontSize: '11px', fontWeight: '700', padding: '2px 10px', borderRadius: '20px' },
  empty: { textAlign: 'center', padding: '40px 20px', color: '#bbb' },

  // Phone indicator
  phoneYes: {
    display: 'inline-flex', alignItems: 'center', gap: '3px',
    padding: '2px 8px', borderRadius: '20px',
    background: '#e8f5e9', color: '#2e7d32', fontSize: '11px', fontWeight: '700', flexShrink: 0,
  },
  phoneNo: {
    display: 'inline-flex', alignItems: 'center', gap: '3px',
    padding: '2px 8px', borderRadius: '20px',
    background: '#fce4ec', color: '#e53935', fontSize: '11px', fontWeight: '700', flexShrink: 0,
  },

  // 2FA indicator
  twoFaBadge: {
    display: 'inline-flex', alignItems: 'center', gap: '3px',
    padding: '2px 8px', borderRadius: '20px',
    fontSize: '11px', fontWeight: '700', flexShrink: 0,
  },
  twoFaActive:  { background: '#e8f5e9', color: '#2e7d32' },
  twoFaPending: { background: '#fff8e1', color: '#f57c00' },
  twoFaNone:    { background: '#fce4ec', color: '#c62828' },
  resetBtn: {
    padding: '6px 12px', background: '#fff8e1', color: '#f57c00',
    borderRadius: '6px', fontSize: '12px', fontWeight: '600',
    border: '1px solid #ffe082', cursor: 'pointer',
  },
  unlockBtn: {
    padding: '6px 12px', background: '#fce4ec', color: '#c62828',
    borderRadius: '6px', fontSize: '12px', fontWeight: '600',
    border: '1px solid #ef9a9a', cursor: 'pointer',
  },
  lockedBadge: {
    display: 'inline-block', padding: '2px 9px', borderRadius: '20px',
    background: '#fce4ec', color: '#c62828', fontSize: '11px', fontWeight: '700',
  },

  // Inline edit / add form
  editForm: {
    borderTop: '1px solid #f0f0f0', marginTop: '12px', paddingTop: '14px',
    display: 'flex', flexDirection: 'column', gap: '10px',
  },
  formRow: { display: 'flex', gap: '10px' },
  input: {
    flex: 1, padding: '9px 12px', borderRadius: '8px',
    border: '1.5px solid #e0e0e0', fontSize: '13px', outline: 'none', boxSizing: 'border-box',
  },
  select: {
    flex: 1, padding: '9px 12px', borderRadius: '8px',
    border: '1.5px solid #e0e0e0', fontSize: '13px', background: '#fff', outline: 'none',
  },
  saveBtn: {
    padding: '9px 18px', background: '#DB3267', color: '#fff',
    borderRadius: '8px', fontSize: '13px', fontWeight: '700', border: 'none', cursor: 'pointer',
  },
  cancelEditBtn: {
    padding: '9px 14px', background: '#f5f4f8', color: '#888',
    borderRadius: '8px', fontSize: '13px', fontWeight: '600', border: 'none', cursor: 'pointer',
  },
  formError: { fontSize: '12px', color: '#e53935' },

  // Add person card
  addCard: {
    background: '#fff', borderRadius: '12px', padding: '18px',
    marginBottom: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
    border: '1.5px solid #f0b8cc',
  },
  addTitle: { fontSize: '15px', fontWeight: '700', color: '#222', marginBottom: '14px' },
  addBtn: {
    width: '100%', padding: '12px', background: '#fff', color: '#DB3267',
    borderRadius: '10px', fontSize: '14px', fontWeight: '600',
    border: '1.5px dashed #f0b8cc', cursor: 'pointer', marginBottom: '16px',
  },
  label: { fontSize: '12px', fontWeight: '700', color: '#777', marginBottom: '3px', display: 'block', textTransform: 'uppercase', letterSpacing: '0.4px' },

  // Groups
  groupCard: {
    background: '#fff', borderRadius: '12px', padding: '16px 18px',
    marginBottom: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer',
  },
  groupName:  { fontSize: '15px', fontWeight: '700', color: '#222' },
  groupCount: { fontSize: '12px', color: '#aaa', marginTop: '2px' },
  delGroupBtn: { padding: '6px 12px', background: '#fff', color: '#e53935', borderRadius: '6px', fontSize: '12px', fontWeight: '600', border: '1px solid #ffcdd2', cursor: 'pointer', flexShrink: 0 },
  backBtn: { display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', color: '#DB3267', fontSize: '14px', fontWeight: '600', cursor: 'pointer', padding: '0 0 16px 0' },
  memberCard: { background: '#fff', borderRadius: '10px', padding: '14px 16px', marginBottom: '8px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' },
  checkbox: { width: '18px', height: '18px', accentColor: '#DB3267', cursor: 'pointer', flexShrink: 0 },
  memberName: { fontSize: '14px', fontWeight: '600', color: '#222' },
  memberMeta: { fontSize: '12px', color: '#aaa' },
  groupSaveBtn: { width: '100%', padding: '13px', background: '#DB3267', color: '#fff', borderRadius: '10px', fontSize: '15px', fontWeight: '700', border: 'none', cursor: 'pointer', marginTop: '16px' },
  newGroupBtn: { width: '100%', padding: '12px', background: '#fff', color: '#DB3267', borderRadius: '10px', fontSize: '14px', fontWeight: '600', border: '1.5px dashed #f0b8cc', cursor: 'pointer', marginTop: '4px' },
  selectionRow: { display: 'flex', gap: '8px', marginBottom: '12px' },
  selectAllBtn: { padding: '6px 14px', background: '#fce8ef', color: '#DB3267', borderRadius: '6px', fontSize: '12px', fontWeight: '600', border: 'none', cursor: 'pointer' },
  clearBtn:     { padding: '6px 14px', background: '#f5f4f8', color: '#888', borderRadius: '6px', fontSize: '12px', fontWeight: '600', border: 'none', cursor: 'pointer' },
  addGroupForm: { background: '#fff', borderRadius: '12px', padding: '16px 18px', marginBottom: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', display: 'flex', gap: '8px', alignItems: 'center' },
  groupInput: { flex: 1, padding: '9px 13px', borderRadius: '8px', border: '1.5px solid #e0e0e0', fontSize: '14px', outline: 'none' },
  groupAddBtn: { padding: '9px 18px', background: '#DB3267', color: '#fff', borderRadius: '8px', fontSize: '13px', fontWeight: '700', border: 'none', cursor: 'pointer' },
  groupCancelBtn: { padding: '9px 14px', background: '#f5f4f8', color: '#888', borderRadius: '8px', fontSize: '13px', fontWeight: '600', border: 'none', cursor: 'pointer' },
};

const statusColour = (status) => {
  if (status === 'approved') return { background: '#e8f5e9', color: '#2e7d32' };
  if (status === 'rejected') return { background: '#fce4ec', color: '#c62828' };
  return { background: '#fff3e0', color: '#e65100' };
};

const cardStyle = (status) => ({
  ...s.card,
  ...(status === 'pending'  ? s.cardPending  : {}),
  ...(status === 'approved' ? s.cardApproved : {}),
  ...(status === 'rejected' ? s.cardRejected : {}),
});

// ── Groups Manager ─────────────────────────────────────────────────────────────

function GroupsManager({ users }) {
  const [groups, setGroups]                 = useState([]);
  const [selectedGroup, setSelectedGroup]   = useState(null);
  const [memberIds, setMemberIds]           = useState(new Set());
  const [saving, setSaving]                 = useState(false);
  const [newGroupName, setNewGroupName]     = useState('');
  const [addingGroup, setAddingGroup]       = useState(false);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [groupError, setGroupError]         = useState('');

  const approvedStaff = users.filter(u => u.status === 'approved' && u.role === 'staff');

  const loadGroups = useCallback(async () => {
    const res  = await apiFetch(`${API}/groups`);
    const data = await res.json();
    if (data.ok) setGroups(data.groups);
  }, []);

  useEffect(() => { loadGroups(); }, [loadGroups]);

  async function openGroup(group) {
    setLoadingMembers(true);
    setSelectedGroup(group);
    const res  = await apiFetch(`${API}/groups/${group.id}/members`);
    const data = await res.json();
    if (data.ok) setMemberIds(new Set(data.userIds));
    setLoadingMembers(false);
  }

  function toggleMember(id) {
    const next = new Set(memberIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setMemberIds(next);
  }

  async function saveMembers() {
    setSaving(true);
    await apiFetch(`${API}/groups/${selectedGroup.id}/members`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ userIds: [...memberIds] }),
    });
    setSaving(false);
    await loadGroups();
    setSelectedGroup(null);
  }

  async function addGroup() {
    if (!newGroupName.trim()) return;
    setGroupError('');
    const res  = await apiFetch(`${API}/groups`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ name: newGroupName.trim() }),
    });
    const data = await res.json();
    if (!data.ok) { setGroupError(data.error); return; }
    setNewGroupName('');
    setAddingGroup(false);
    await loadGroups();
  }

  async function deleteGroup(e, id) {
    e.stopPropagation();
    if (!window.confirm('Delete this group? Members will not be deleted.')) return;
    await apiFetch(`${API}/groups/${id}`, { method: 'DELETE' });
    await loadGroups();
  }

  if (selectedGroup) {
    return (
      <div>
        <button style={s.backBtn} onClick={() => setSelectedGroup(null)}>← {selectedGroup.name}</button>
        {loadingMembers ? (
          <div style={s.empty}>Loading...</div>
        ) : (
          <>
            <div style={s.selectionRow}>
              <button style={s.selectAllBtn} onClick={() => setMemberIds(new Set(approvedStaff.map(u => u.id)))}>Select All</button>
              <button style={s.clearBtn} onClick={() => setMemberIds(new Set())}>Clear All</button>
              <span style={{ fontSize: '12px', color: '#aaa', alignSelf: 'center', marginLeft: 'auto' }}>{memberIds.size} selected</span>
            </div>
            {approvedStaff.length === 0 ? (
              <div style={s.empty}>No approved people yet</div>
            ) : (
              approvedStaff.map(u => (
                <div key={u.id} style={s.memberCard} onClick={() => toggleMember(u.id)}>
                  <input type="checkbox" style={s.checkbox} checked={memberIds.has(u.id)} onChange={() => toggleMember(u.id)} onClick={e => e.stopPropagation()} />
                  <div>
                    <div style={s.memberName}>{u.name}</div>
                    <div style={s.memberMeta}>{u.shift ? `${u.shift} shift` : 'No shift'}{u.phone ? ` · ${u.phone}` : ' · No phone'}</div>
                  </div>
                </div>
              ))
            )}
            <button style={s.groupSaveBtn} onClick={saveMembers} disabled={saving}>
              {saving ? 'Saving...' : 'Save Group Members'}
            </button>
          </>
        )}
      </div>
    );
  }

  return (
    <div>
      {groups.map(g => (
        <div key={g.id} style={s.groupCard} onClick={() => openGroup(g)}>
          <div>
            <div style={s.groupName}>{g.name}</div>
            <div style={s.groupCount}>{g.member_count} member{g.member_count !== 1 ? 's' : ''}</div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button style={s.editBtn} onClick={() => openGroup(g)}>Edit Members</button>
            <button style={s.delGroupBtn} onClick={e => deleteGroup(e, g.id)}>Delete</button>
          </div>
        </div>
      ))}
      {addingGroup ? (
        <div style={s.addGroupForm}>
          <input style={s.groupInput} value={newGroupName} onChange={e => setNewGroupName(e.target.value)} onKeyDown={e => e.key === 'Enter' && addGroup()} placeholder="Group name..." autoFocus />
          <button style={s.groupAddBtn} onClick={addGroup}>Add</button>
          <button style={s.groupCancelBtn} onClick={() => { setAddingGroup(false); setNewGroupName(''); setGroupError(''); }}>Cancel</button>
        </div>
      ) : (
        <button style={s.newGroupBtn} onClick={() => setAddingGroup(true)}>+ New Group</button>
      )}
      {groupError && <div style={{ color: '#e53935', fontSize: '13px', marginTop: '8px' }}>{groupError}</div>}
    </div>
  );
}

// ── Add Person form ────────────────────────────────────────────────────────────

const EMPTY_ADD = { name: '', username: '', password: '', shift: 'Day', phone: '' };

function AddPersonForm({ onSaved, onCancel }) {
  const [form, setForm]   = useState(EMPTY_ADD);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  function set(field, value) { setForm(f => ({ ...f, [field]: value })); }

  async function save() {
    if (!form.name.trim() || !form.username.trim() || !form.password.trim()) {
      setError('Name, username and password are required.');
      return;
    }
    setError('');
    setSaving(true);
    const res  = await apiFetch(`${API}/users`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({
        name:        form.name.trim(),
        username:    form.username.trim().toLowerCase().replace(/\s+/g, ''),
        password:    form.password,
        shift:       form.shift || null,
        phone:       form.phone.trim() || null,
        adminCreate: true,
      }),
    });
    const data = await res.json();
    setSaving(false);
    if (!data.ok) { setError(data.error); return; }
    onSaved();
  }

  return (
    <div style={s.addCard}>
      <div style={s.addTitle}>Add New Person</div>

      <label style={s.label}>Full name</label>
      <input style={{ ...s.input, marginBottom: '10px', width: '100%', boxSizing: 'border-box' }} value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Sarah Johnson" />

      <div style={s.formRow}>
        <div style={{ flex: 1 }}>
          <label style={s.label}>Username</label>
          <input style={s.input} value={form.username} onChange={e => set('username', e.target.value)} placeholder="e.g. sarahj" />
        </div>
        <div style={{ flex: 1 }}>
          <label style={s.label}>Password</label>
          <input style={s.input} type="text" value={form.password} onChange={e => set('password', e.target.value)} placeholder="Temporary password" />
        </div>
      </div>

      <div style={{ ...s.formRow, marginTop: '10px' }}>
        <div style={{ flex: 1 }}>
          <label style={s.label}>Shift</label>
          <select style={s.select} value={form.shift} onChange={e => set('shift', e.target.value)}>
            <option value="Day">Day</option>
            <option value="Night">Night</option>
            <option value="">None</option>
          </select>
        </div>
        <div style={{ flex: 1 }}>
          <label style={s.label}>Mobile number</label>
          <input style={s.input} type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+44..." />
        </div>
      </div>

      {error && <div style={{ ...s.formError, marginTop: '8px' }}>{error}</div>}

      <div style={{ ...s.actions, marginTop: '14px' }}>
        <button style={s.saveBtn} onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Add Person'}</button>
        <button style={s.cancelEditBtn} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

// ── Person card with inline edit ───────────────────────────────────────────────

function twoFaStatus(u) {
  if (!u.phone)                    return { label: '✗ 2FA', style: s.twoFaNone,    tip: 'No mobile — add a number to enable 2FA' };
  if (u.trustedDeviceCount > 0)    return { label: '✓ 2FA', style: s.twoFaActive,  tip: '2FA active — device verified' };
  return                                  { label: '~ 2FA', style: s.twoFaPending, tip: 'Phone added but 2FA not yet verified' };
}

function PersonCard({ u, onApprove, onReject, onSaved }) {
  const [editing, setEditing]     = useState(false);
  const [form, setForm]           = useState({ name: u.name, phone: u.phone || '', shift: u.shift || '' });
  const [saving, setSaving]       = useState(false);
  const [resetting, setResetting] = useState(false);
  const [unlocking, setUnlocking] = useState(false);

  const isLocked = u.lockedUntil && u.lockedUntil > new Date().toISOString();

  function set(field, value) { setForm(f => ({ ...f, [field]: value })); }

  async function save() {
    setSaving(true);
    await apiFetch(`${API}/users/${u.id}`, {
      method:  'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ action: 'update', name: form.name.trim(), phone: form.phone.trim() || null, shift: form.shift || null }),
    });
    setSaving(false);
    setEditing(false);
    onSaved();
  }

  async function reset2FA() {
    if (!window.confirm(`Reset 2FA for ${u.name}? They will need to verify again on their next login.`)) return;
    setResetting(true);
    await apiFetch(`${API}/users/${u.id}/trusted-devices`, { method: 'DELETE' });
    setResetting(false);
    onSaved();
  }

  async function unlock() {
    setUnlocking(true);
    await apiFetch(`${API}/users/${u.id}`, {
      method:  'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ action: 'unlock' }),
    });
    setUnlocking(false);
    onSaved();
  }

  const tfa = twoFaStatus(u);

  return (
    <div style={cardStyle(u.status)}>
      <div style={s.topRow}>
        <div style={s.nameRow}>
          <span style={u.phone ? s.phoneYes : s.phoneNo} title={u.phone ? `Mobile: ${u.phone}` : 'No mobile number'}>
            {u.phone ? '✓ Mobile Active' : '✗ No Mobile'}
          </span>
          <span style={{ ...s.twoFaBadge, ...tfa.style }} title={tfa.tip}>
            {tfa.label}
          </span>
          {isLocked && <span style={s.lockedBadge}>Locked</span>}
          <div style={s.name}>{u.name}</div>
        </div>
        <span style={{ ...s.statusBadge, ...statusColour(u.status) }}>{u.status}</span>
      </div>

      <div style={s.meta}>
        @{u.username} · {u.shift ? u.shift + ' shift' : 'Admin'} · Joined {u.createdAt?.slice(0, 10)}
        {u.phone ? ` · ${u.phone}` : ' · No mobile'}
        {u.lastLogin ? ` · Last login ${u.lastLogin.slice(0, 16)}` : ''}
      </div>

      {!editing && (
        <div style={s.actions}>
          {u.status === 'pending' && (
            <>
              <button style={s.approveBtn} onClick={() => onApprove(u.id)}>✓ Approve</button>
              <button style={s.rejectBtn}  onClick={() => onReject(u.id)}>✕ Reject</button>
            </>
          )}
          {isLocked && (
            <button style={s.unlockBtn} onClick={unlock} disabled={unlocking}>
              {unlocking ? 'Unlocking...' : 'Unlock Account'}
            </button>
          )}
          {u.role !== 'admin' && (
            <button style={s.editBtn} onClick={() => { setForm({ name: u.name, phone: u.phone || '', shift: u.shift || '' }); setEditing(true); }}>
              Edit
            </button>
          )}
          {u.phone && u.role !== 'admin' && (
            <button style={s.resetBtn} onClick={reset2FA} disabled={resetting}>
              {resetting ? 'Resetting...' : 'Reset 2FA'}
            </button>
          )}
        </div>
      )}

      {editing && (
        <div style={s.editForm}>
          <div style={s.formRow}>
            <div style={{ flex: 1 }}>
              <label style={s.label}>Name</label>
              <input style={s.input} value={form.name} onChange={e => set('name', e.target.value)} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={s.label}>Shift</label>
              <select style={s.select} value={form.shift} onChange={e => set('shift', e.target.value)}>
                <option value="Day">Day</option>
                <option value="Night">Night</option>
                <option value="">None</option>
              </select>
            </div>
          </div>
          <div>
            <label style={s.label}>Mobile number</label>
            <input style={{ ...s.input, width: '100%', boxSizing: 'border-box' }} type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+44..." />
          </div>
          <div style={s.actions}>
            <button style={s.saveBtn} onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
            <button style={s.cancelEditBtn} onClick={() => setEditing(false)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function AdminUsers() {
  const { users, approveUser, rejectUser, refresh } = useSubmissions();
  const [filter, setFilter]       = useState('pending');
  const [showAddForm, setShowAddForm] = useState(false);

  const pendingCount = users.filter(u => u.status === 'pending').length;
  const filtered = filter === 'groups'
    ? []
    : filter === 'all' ? users : users.filter(u => u.status === filter);

  async function handleSaved() {
    if (refresh) await refresh();
  }

  return (
    <div style={s.page}>
      <h1 style={s.heading}>
        People
        {pendingCount > 0 && <span style={s.badge}>{pendingCount} pending</span>}
      </h1>
      <p style={s.sub}>Manage people, access, and groups</p>

      {/* QR Code */}
      <div style={s.qrCard}>
        <img
          style={s.qrImage}
          src={`https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(REGISTER_URL)}`}
          alt="Registration QR Code"
        />
        <div style={s.qrInfo}>
          <div style={s.qrTitle}>Self-Registration QR Code</div>
          <div style={s.qrSub}>
            Show to new staff — they scan to request access. You approve them here before they can log in.
          </div>
          <div style={s.qrUrl}>{REGISTER_URL}</div>
        </div>
      </div>

      {/* Filter tabs */}
      <div style={s.tabs}>
        {[
          { key: 'pending',  label: `Pending${pendingCount > 0 ? ` (${pendingCount})` : ''}` },
          { key: 'approved', label: 'Approved' },
          { key: 'all',      label: 'All' },
          { key: 'groups',   label: 'Groups' },
        ].map(({ key, label }) => (
          <button key={key} style={{ ...s.tab, ...(filter === key ? s.tabActive : {}) }} onClick={() => setFilter(key)}>
            {label}
          </button>
        ))}
      </div>

      {/* Groups tab */}
      {filter === 'groups' ? (
        <GroupsManager users={users} />
      ) : (
        <>
          {/* Add person */}
          {!showAddForm && filter !== 'pending' && (
            <button style={s.addBtn} onClick={() => setShowAddForm(true)}>+ Add Person</button>
          )}
          {showAddForm && (
            <AddPersonForm
              onSaved={() => { setShowAddForm(false); handleSaved(); }}
              onCancel={() => setShowAddForm(false)}
            />
          )}

          {/* People list */}
          {filtered.length === 0 ? (
            <div style={s.empty}>No {filter} people</div>
          ) : (
            filtered.map(u => (
              <PersonCard
                key={u.id}
                u={u}
                onApprove={approveUser}
                onReject={rejectUser}
                onSaved={handleSaved}
              />
            ))
          )}
        </>
      )}
    </div>
  );
}
